/**
 * post-grad-banner.tsx — NL-047 post-graduation Dashboard banner.
 *
 * Renders for 7 calendar days after the client's `actual_graduation`
 * date. Shows a thank-you message + an HOS download link the coach
 * surfaces manually (the HOS export itself lives in the macOS coach
 * app; this banner just celebrates the moment + reassures the client
 * the HOS will arrive shortly).
 *
 * Mounted on /dashboard before the existing header. Returns null
 * outside the 7-day window OR when no graduation date is set.
 */

import { useTranslation } from 'react-i18next';

interface Props {
  /** ISO YYYY-MM-DD of the graduation date, or null when not graduated yet. */
  graduationDate: string | null | undefined;
  /** Today's local-day ISO. Injected for testability. Defaults to today. */
  today?: string;
}

const POST_GRAD_WINDOW_DAYS = 7;

/** Returns the number of full days from `fromIso` to `toIso` (toIso - fromIso). */
function daysBetween(fromIso: string, toIso: string): number {
  const a = new Date(`${fromIso}T00:00:00.000Z`).getTime();
  const b = new Date(`${toIso}T00:00:00.000Z`).getTime();
  return Math.round((b - a) / (24 * 60 * 60 * 1000));
}

/** Format YYYY-MM-DD for display per locale (de = DD.MM.YYYY, en = MM/DD/YYYY). */
function formatDate(iso: string, lang: string): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getUTCFullYear());
  return lang === 'de' ? `${dd}.${mm}.${yyyy}` : `${mm}/${dd}/${yyyy}`;
}

export function PostGradBanner({ graduationDate, today }: Props): JSX.Element | null {
  const { t, i18n } = useTranslation();
  if (!graduationDate) return null;
  const nowIso = today ?? new Date().toISOString().slice(0, 10);
  const elapsed = daysBetween(graduationDate, nowIso);
  // Visible from grad-day (elapsed=0) through day +6 inclusive (window length 7 days).
  if (elapsed < 0 || elapsed >= POST_GRAD_WINDOW_DAYS) return null;
  const formatted = formatDate(graduationDate, i18n.language);

  return (
    <section
      data-testid="post-grad-banner"
      className="rounded-2xl border border-flame/40 bg-flame/[0.06] p-5 shadow-sm"
    >
      <header className="flex items-start gap-s3">
        <span aria-hidden="true" className="text-[28px] leading-none">🎓</span>
        <div className="flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-flame-deep mb-s1">
            {t('dashboard.postGrad.eyebrow')}
          </p>
          <h2 className="font-display text-lg font-semibold text-ink">
            {t('dashboard.postGrad.title', { date: formatted })}
          </h2>
          <p className="mt-s2 text-sm text-gray-2 max-w-prose">
            {t('dashboard.postGrad.body')}
          </p>
          <p className="mt-s3 text-xs italic text-gray-1">
            {t('dashboard.postGrad.hint')}
          </p>
        </div>
      </header>
    </section>
  );
}
